import Button from '@/src/components/atom/Button';
import Checkbox from '@/src/components/atom/Checkbox';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import Label from '@/src/components/atom/Label';
import RadioGroup from '@/src/components/atom/RadioGroup';
import Select from '@/src/components/atom/Select';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { useEffect, useRef, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getPartnershipLinksByIdAPI,
  putPartnershipLinksAPI,
} from '@/src/apis/rest/partnershipCompanies';
import dayjs from 'dayjs';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PutPartnershipLinksRequestBody } from '@/src/apis/rest/partnershipCompaines.dtos';
import InfoFormSectionCard from '@/src/components/organism/InfoFormSectionCard/InfoFormSectionCard';
import { DateRange } from 'react-day-picker';
import {
  getRecyclingPackageProductsAPI,
  getRecyclingProductsAPI,
} from '@/src/apis/rest/recyclingProducts';
import { getRecyclingCouponsAPI } from '@/src/apis/rest/recyclingCoupon';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { usePrompt } from '@/src/hooks/usePrompt';

const BACK_PATH = PATH.PARTNERSHIP_LINKS_DETAIL;

const partnershipLinksFormSchema = () => {
  return z
    .object({
      startedAt: z.string().min(1),
      endedAt: z.string().min(1),
      isEntryAvailable: z.boolean(),
      isDonationAvailable: z.boolean(),
      recyclingCouponId: z.number(),
      recyclingPackageProductId: z.number(),
      recyclingCommonProductIds: z.array(z.string()).optional().nullable(),
      recyclingEventProductIds: z.array(z.string()).optional().nullable(),
      maxRecyclingPackageProductCount: z.number(),
      minimumRequestWeight: z.number(),
      generatePartnershipRecyclingCautionDto: z.array(
        z.object({ id: z.number().optional(), contents: z.string().min(1) }),
      ),
      generatePartnershipRecyclingInfoDto: z.array(
        z.object({ id: z.number().optional(), contents: z.string().min(1) }),
      ),
      removeInfoIds: z.array(z.number()).optional(),
      removeCautionIds: z.array(z.number()).optional(),
    })
    .refine(data => {
      return (
        data.recyclingCommonProductIds?.length ||
        data.recyclingEventProductIds?.length
      );
    });
};

const DEFAULT_VALUES: Partial<
  PutPartnershipLinksRequestBody & {
    postedAt: DateRange;
    recyclingCommonProductIds: string[];
    recyclingEventProductIds: string[];
  }
> = {
  postedAt: { from: undefined, to: undefined },
  recyclingCommonProductIds: [],
  recyclingEventProductIds: [],
};

const PartnershipLinkEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [defaultData, setDefaultData] = useState('');

  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const { data: partnershipLinkData } = useQuery({
    queryKey: ['partnership-links', id],
    queryFn: () => getPartnershipLinksByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
  });

  const form = useForm<
    PutPartnershipLinksRequestBody & {
      postedAt: DateRange;
      additionMenu: string[];
      isCustomer: boolean;
      recyclingCommonProductIds: string[];
      recyclingEventProductIds: string[];
    }
  >({
    resolver: zodResolver(partnershipLinksFormSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });
  const startedAtController = useController({
    name: 'startedAt',
    control: form.control,
  });
  const endedAtController = useController({
    name: 'endedAt',
    control: form.control,
  });

  const { data: recyclingPackageProductsOptions } = useQuery({
    queryKey: ['recycling-package-products'],
    queryFn: () => getRecyclingPackageProductsAPI({}),
    select: data =>
      (data?.rows || []).map(item => ({
        label: item.name,
        value: String(item.id),
      })),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: recyclingProductsCommonOptions } = useQuery({
    queryKey: ['recycling-products'],
    queryFn: () =>
      getRecyclingProductsAPI({
        isExposed: true,
        isEventProduct: false,
      }),
    select: data =>
      (data?.rows || []).map(item => ({
        label: item.category,
        value: String(item.id),
      })),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: recyclingProductsEventOptions } = useQuery({
    queryKey: ['recycling-products-events'],
    queryFn: () =>
      getRecyclingProductsAPI({
        isEventProduct: true,
      }),
    select: data =>
      (data?.rows || []).map(item => ({
        label: item.category,
        value: String(item.id),
      })),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: recyclingCouponsOptions } = useQuery({
    queryKey: ['recycling-coupons'],
    queryFn: () =>
      getRecyclingCouponsAPI({
        isExposed: true,
      }),
    select: data =>
      (data?.rows || []).map(item => ({
        label: item.sponsorName,
        value: String(item.id),
      })),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const putPartnershipLinksMutation = useMutation({
    mutationFn: putPartnershipLinksAPI,
  });

  const formRef = useRef<HTMLFormElement>(null);

  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };

  const handleSubmit = (
    values: PutPartnershipLinksRequestBody & {
      postedAt?: DateRange;
      additionMenu?: string[];
      recyclingCommonProductIds?: string[];
      recyclingEventProductIds?: string[];
    },
  ) => {
    const newValues = {
      ...values,
      maxRecyclingPackageProductCount: Number(
        values.maxRecyclingPackageProductCount,
      ),
      minimumRequestWeight: Number(values.minimumRequestWeight),
      recyclingProductIds: [
        ...(values?.recyclingCommonProductIds?.filter(
          (value: string) => value !== 'AllCommon',
        ) || []),
        ...(values?.recyclingEventProductIds?.filter(
          (value: string) => value !== 'AllEvent',
        ) || []),
      ],
    };

    delete newValues.postedAt;
    delete newValues.additionMenu;
    delete newValues.recyclingCommonProductIds;
    delete newValues.recyclingEventProductIds;

    putPartnershipLinksMutation.mutate(
      { ...newValues, id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH.replace(':id', String(id)));
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  const handleAdditionMenuChange = (type: string, value: boolean) => {
    switch (type) {
      case 'donate':
        form.setValue('isDonationAvailable', value);
        break;
      case 'entry':
        form.setValue('isEntryAvailable', value);
        break;
      default:
        break;
    }
  };
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => {
              navigate(-1);
            }}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            type="button"
            onClick={openEditDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={putPartnershipLinksMutation.isPending}
            disabled={
              !form.formState.isValid ||
              !(defaultData !== JSON.stringify(form.watch()))
            }
          >
            {BUTTON_CONST.save}
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  useEffect(() => {
    if (partnershipLinkData) {
      const additionMenuArray = [];

      if (partnershipLinkData?.isDonationAvailable) {
        additionMenuArray.push('donate');
      }
      if (partnershipLinkData?.isEntryAvailable) {
        additionMenuArray.push('entry');
      }

      const recyclingProductCommonIdsArray =
        partnershipLinkData?.recyclingProducts
          .filter(item => !item.isEventProduct && item.isExposed)
          .map(item => String(item.id));
      const recyclingProductEventIdsArray =
        partnershipLinkData?.recyclingProducts
          .filter(item => item.isEventProduct)
          .map(item => String(item.id));

      const resetFormData = {
        ...partnershipLinkData,
        postedAt: {
          from: new Date(partnershipLinkData?.startedAt),
          to: new Date(partnershipLinkData?.endedAt),
        },
        generatePartnershipRecyclingCautionDto:
          partnershipLinkData?.partnershipRecyclingCaution || [],
        generatePartnershipRecyclingInfoDto:
          partnershipLinkData?.partnershipRecyclingInfo || [],
        removeInfoIds: [],
        removeCautionIds: [],
        isCustomer: partnershipLinkData?.partnershipRecyclingOption?.isCustomer,
        additionMenu: additionMenuArray,
        maxRecyclingPackageProductCount:
          partnershipLinkData?.partnershipRecyclingOption
            ?.maxRecyclingPackageProductCount,
        minimumRequestWeight:
          partnershipLinkData?.partnershipRecyclingOption?.minimumRequestWeight,
        recyclingPackageProductId:
          partnershipLinkData?.partnershipRecyclingOption
            ?.recyclingPackageProduct?.id,
        recyclingCouponId:
          partnershipLinkData?.partnershipRecyclingOption?.recyclingCoupon?.id,
        recyclingCommonProductIds:
          recyclingProductsCommonOptions?.length ===
          recyclingProductCommonIdsArray?.length
            ? [...recyclingProductCommonIdsArray, 'AllCommon']
            : recyclingProductCommonIdsArray,
        recyclingEventProductIds:
          recyclingProductsEventOptions?.length ===
          recyclingProductEventIdsArray?.length
            ? [...recyclingProductEventIdsArray, 'AllEvent']
            : recyclingProductEventIdsArray,
      };

      console.log('resetFormData >>>', resetFormData);

      form.reset(resetFormData);

      setDefaultData(JSON.stringify(resetFormData));
    }
  }, [partnershipLinkData, form]);

  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  return (
    <>
      <MainContentTitle
        pageTitle="제휴사 링크 수정"
        breadcrumbList={['제휴사 링크 관리', '제휴사 링크 수정']}
      />
      {partnershipLinkData && (
        <Form {...form}>
          <form
            ref={formRef}
            className="mb-[100px] flex flex-col gap-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <SectionCard
              cardTitle="제휴사 링크 기본 정보"
              className="col-span-2"
            >
              <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="postedAt"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent isRequired label="게시 기간" error={error}>
                          <DateRangePicker
                            {...field}
                            onChange={e => {
                              field.onChange(e);
                              startedAtController.field.onChange(
                                e?.from
                                  ? dayjs(e?.from).format('YYYY-MM-DD')
                                  : '',
                              );
                              endedAtController.field.onChange(
                                e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                              );
                            }}
                            dateDisabledMatcher={getDateDisabledMatcher}
                          />
                        </FormContent>
                      );
                    }}
                  />

                  <SectionLabel
                    labelProps={{
                      className: 'translate-y-2',
                    }}
                    label="제휴사명"
                    isRequired
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={partnershipLinkData?.partnershipCompany?.name}
                        disabled
                      />
                    }
                  />
                </div>
                <div className="border-r border-r-gray1" />
                <div className="flex flex-col gap-6">
                  <SectionLabel
                    labelProps={{
                      className: 'translate-y-2',
                    }}
                    label="URL code"
                    isRequired
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={partnershipLinkData?.urlCode}
                        disabled
                      />
                    }
                  />
                  <FormField
                    control={form.control}
                    name="additionMenu"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="추가 메뉴" error={error}>
                          <div className="flex gap-x-4">
                            {(
                              [
                                { value: 'entry', label: '응모하기' },
                                { value: 'donate', label: '기부하기' },
                              ] as const
                            )?.map((item, idx) => {
                              return (
                                <div
                                  className="flex items-center gap-x-2"
                                  key={idx.toString()}
                                >
                                  <Checkbox
                                    id={`${item.value}-${item.label}`}
                                    checked={field.value?.includes(item.value)}
                                    onCheckedChange={checked => {
                                      handleAdditionMenuChange(
                                        item.value,
                                        !!checked,
                                      );

                                      checked
                                        ? field.onChange([
                                            ...(field.value || []),
                                            item.value,
                                          ])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value: string) =>
                                                value !== item.value,
                                            ),
                                          );
                                    }}
                                  />
                                  <Label
                                    htmlFor={`${item.value}-${item.label}`}
                                    className="text-b16r text-gray6"
                                  >
                                    {item.label}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        </FormContent>
                      );
                    }}
                  />
                </div>
              </div>
            </SectionCard>
            <SectionCard
              cardTitle="제휴사 링크 수거신청 정보"
              className="col-span-2"
            >
              <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="isCustomer"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="적용 회원 유형" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: '회원', value: 'true' },
                              { label: '비회원', value: 'false' },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => field.onChange(e === 'true'),
                              disabled: true,
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="recyclingPackageProductId"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          isRequired
                          label="수거 기본 품목 선택"
                          error={error}
                        >
                          <Select
                            option={[
                              {
                                optionList:
                                  recyclingPackageProductsOptions || [],
                              },
                            ]}
                            rootProps={{
                              value: String(field.value),
                              onValueChange: value => {
                                if (value !== '') field.onChange(Number(value));
                              },
                            }}
                            valueProps={{
                              placeholder:
                                '기본으로 수거되는 품목을 선택해주세요',
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="maxRecyclingPackageProductCount"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          isRequired
                          label="수거 기본 품목 최대 개수"
                          error={error}
                          labelClassName="max-w-[166px] w-[166px]"
                        >
                          <Input
                            type="number"
                            min={0}
                            onClear={() => field.onChange('')}
                            placeholder="수거 기본 품목 최대 개수를 입력해주세요"
                            {...field}
                            onChange={e =>
                              field.onChange(
                                e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              )
                            }
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <div className="border-r border-r-gray1" />
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="minimumRequestWeight"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          isRequired
                          label="수거 신청 최소 무게(g)"
                          error={error}
                        >
                          <Input
                            onClear={() => field.onChange('')}
                            placeholder="최소 무게를 입력해주세요 (g 제외)"
                            {...field}
                            type="number"
                            min={0}
                            onChange={e =>
                              field.onChange(
                                e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              )
                            }
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="recyclingCommonProductIds"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="수거 품목 선택" error={error}>
                          <div className="flex flex-wrap gap-x-4 gap-y-4">
                            {[
                              { value: 'AllCommon', label: '전체' },
                              ...(recyclingProductsCommonOptions ?? []),
                            ]?.map((item, idx) => {
                              return (
                                <div
                                  className="flex items-center gap-x-2"
                                  key={idx.toString()}
                                >
                                  <Checkbox
                                    id={`${item.value}-${item.label}`}
                                    checked={field.value?.includes(item.value)}
                                    onCheckedChange={checked => {
                                      let array: string[] = [];
                                      recyclingProductsCommonOptions?.map(
                                        item => {
                                          array = [...array, item.value];
                                        },
                                      );

                                      if (item.value === 'AllCommon') {
                                        checked
                                          ? field.onChange([
                                              ...array,
                                              'AllCommon',
                                            ])
                                          : field.onChange([]);
                                      } else {
                                        if (checked) {
                                          field.value?.length ===
                                          array?.length - 1
                                            ? field.onChange([
                                                ...array,
                                                'AllCommon',
                                              ])
                                            : field.onChange([
                                                ...(field.value || []),
                                                item.value,
                                              ]);
                                        } else {
                                          field.value?.length ===
                                          array?.length + 1
                                            ? field.onChange(
                                                field.value?.filter(
                                                  (value: string) =>
                                                    value !== item.value &&
                                                    value !== 'AllCommon',
                                                ),
                                              )
                                            : field.onChange(
                                                field.value?.filter(
                                                  (value: string) =>
                                                    value !== item.value,
                                                ),
                                              );
                                        }
                                      }
                                    }}
                                  />
                                  <Label
                                    htmlFor={`${item.value}-${item.label}`}
                                    className="text-b16r text-gray6"
                                  >
                                    {item.label}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="recyclingEventProductIds"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="이벤트 품목 선택" error={error}>
                          <div className="flex flex-wrap gap-x-4 gap-y-4">
                            {[
                              { value: 'AllEvent', label: '전체' },
                              ...(recyclingProductsEventOptions ?? []),
                            ]?.map((item, idx) => {
                              return (
                                <div
                                  className="flex items-center gap-x-2"
                                  key={idx.toString()}
                                >
                                  <Checkbox
                                    id={`${item.value}-${item.label}`}
                                    checked={field.value?.includes(item.value)}
                                    onCheckedChange={checked => {
                                      let array: string[] = [];
                                      recyclingProductsEventOptions?.map(
                                        item => {
                                          array = [...array, item.value];
                                        },
                                      );

                                      if (item.value === 'AllEvent') {
                                        checked
                                          ? field.onChange([
                                              ...array,
                                              'AllEvent',
                                            ])
                                          : field.onChange([]);
                                      } else {
                                        if (checked) {
                                          field.value?.length ===
                                          array?.length - 1
                                            ? field.onChange([
                                                ...array,
                                                'AllEvent',
                                              ])
                                            : field.onChange([
                                                ...(field.value || []),
                                                item.value,
                                              ]);
                                        } else {
                                          field.value?.length ===
                                          array?.length + 1
                                            ? field.onChange(
                                                field.value?.filter(
                                                  (value: string) =>
                                                    value !== item.value &&
                                                    value !== 'AllEvent',
                                                ),
                                              )
                                            : field.onChange(
                                                field.value?.filter(
                                                  (value: string) =>
                                                    value !== item.value,
                                                ),
                                              );
                                        }
                                      }
                                    }}
                                  />
                                  <Label
                                    htmlFor={`${item.value}-${item.label}`}
                                    className="text-b16r text-gray6"
                                  >
                                    {item.label}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="recyclingCouponId"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          isRequired
                          label="배송비 쿠폰 선택"
                          error={error}
                        >
                          <Select
                            rootProps={{
                              value: String(field.value),
                              onValueChange: value => {
                                if (value !== '') field.onChange(Number(value));
                              },
                            }}
                            valueProps={{
                              placeholder: '노출될 배송비 쿠폰을 선택해주세요',
                            }}
                            option={[
                              {
                                optionList: recyclingCouponsOptions || [],
                              },
                            ]}
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
              </div>
            </SectionCard>
            <div className="grid grid-cols-[1fr_1fr] gap-4">
              <InfoFormSectionCard
                cardTitle="수거신청 안내사항 영역"
                form={form}
                field="generatePartnershipRecyclingInfoDto"
                removeField="removeInfoIds"
              />
              <InfoFormSectionCard
                cardTitle="수거신청 유의사항 영역"
                form={form}
                field="generatePartnershipRecyclingCautionDto"
                removeField="removeCautionIds"
                buttonText="유의사항 추가하기"
              />
            </div>
            {renderBottomFixedContent()}
          </form>
        </Form>
      )}

      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putPartnershipLinksMutation.isPending}
        />
      )}
    </>
  );
};

export default PartnershipLinkEditPage;
